/** Footer "Add to workout" / "Save" button in the Add Exercise modal. */
export const ADD_EXERCISE_PRIMARY_ACTION_ID = "add-exercise-primary-action";

/** Closed summary of the exercise picker. */
export const ADD_EXERCISE_PICKER_ID = "add-exercise-picker";

/**
 * Enter inside a set field runs the next logical step (the caller decides which).
 * Modified chords, IME composition, newlines in a textarea, and controls that
 * already have an Enter behavior (buttons, rest time, load-mode radios, unit
 * radios) are left alone.
 * preventDefault + stopPropagation so the key cannot implicitly submit a form.
 * In the program editor, Enter in a sets field otherwise activates the first
 * submit button in `#quick-edit` — the copy control — which flashes
 * "Successfully copied" and opens the Clipboard tab.
 *
 * @param {KeyboardEvent | { key: string, target: EventTarget, preventDefault: () => void, stopPropagation: () => void, shiftKey?: boolean, altKey?: boolean, ctrlKey?: boolean, metaKey?: boolean, nativeEvent?: { isComposing?: boolean } }} event
 * @param {(event: KeyboardEvent) => void} commit
 */
export function handleSetEntryKeyDown(event, commit) {
  if (event.key !== "Enter") return;
  if (
    event.nativeEvent?.isComposing ||
    event.shiftKey ||
    event.altKey ||
    event.ctrlKey ||
    event.metaKey
  ) {
    return;
  }
  const target = event.target;
  if (!(target instanceof Element)) return;
  if (
    target.closest(
      "textarea, button, a, .time-selector, .option-toggler, .units-toggle"
    )
  ) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  commit(event);
}

/**
 * Block Enter from implicitly submitting the exercise form when it is pressed
 * in a field that is not the set row (the set row commits itself).
 * Textareas keep their newline. Buttons and links keep their own activation.
 *
 * @param {KeyboardEvent} event
 */
export function preventImplicitEnterSubmit(event) {
  if (event.key !== "Enter") return;
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  if (target.tagName === "TEXTAREA") return;
  if (target.closest("button, a, .add-set-wrapper")) return;
  event.preventDefault();
  event.stopPropagation();
}

/**
 * After a set is committed, focus returns to the Sets field so the next set can
 * be typed immediately. "primary" (Complexes tab) moves it to the footer primary
 * action instead; "none" leaves focus alone (the commit is part of a larger step).
 *
 * @param {"sets" | "primary" | "none"} focusAfter
 */
export function focusAfterSetCommit(focusAfter) {
  if (focusAfter === "none") return;
  window.setTimeout(() => {
    if (focusAfter === "primary") {
      const primary = document.getElementById(ADD_EXERCISE_PRIMARY_ACTION_ID);
      if (primary instanceof HTMLElement && !primary.disabled) {
        primary.focus();
        return;
      }
    }
    const sets = document.getElementById("exerciseAddSets");
    if (sets instanceof HTMLElement) {
      sets.focus();
      return;
    }
    const first = document.querySelector(
      ".add-set-wrapper input:not([type='hidden']):not([type='radio']), .add-set-wrapper select, .add-set-wrapper textarea"
    );
    if (first instanceof HTMLElement) first.focus();
  }, 0);
}

/** After Add to workout, focus moves to the exercise picker. */
export function focusExercisePicker() {
  window.setTimeout(() => {
    const picker = document.getElementById(ADD_EXERCISE_PICKER_ID);
    if (picker instanceof HTMLElement) picker.focus();
  }, 0);
}
